import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { DEFAULT_LOCALE, resolveLocale, type Locale } from '@talk2nick/core';
import ko from './locales/ko.json';
import en from './locales/en.json';

/**
 * 로케일 번들 정책 (PLAN 5-7장):
 * - ko/en: 초기 번들에 동봉 (앱 첫 화면이这两种 중 하나일 확률 높음)
 * - ja/zh/vi/es: 동적 import로 초기 번들 크기 절감
 *
 * 이렇게 하면 ko 사용자 초기 JS에 6개 로케일 전체가 들어가지 않는다.
 */
const LAZY_LOADERS: Partial<Record<Locale, () => Promise<object>>> = {
  ja: () => import('./locales/ja.json').then((m) => m.default),
  zh: () => import('./locales/zh.json').then((m) => m.default),
  vi: () => import('./locales/vi.json').then((m) => m.default),
  es: () => import('./locales/es.json').then((m) => m.default),
};

export const ALL_LOCALES: Locale[] = ['ko', 'en', 'ja', 'zh', 'vi', 'es'];

/** 초기 번들에 동봉되는 로케일 (ko/en) */
export const EAGER_LOCALES: Locale[] = ['ko', 'en'];

let initialized = false;

export function initI18n(userLocale?: string): typeof i18n {
  if (initialized) return i18n;

  const initial = resolveLocale(userLocale, getNavigatorLanguages());

  void i18n.use(initReactI18next).init({
    resources: {
      ko: { translation: ko },
      en: { translation: en },
    },
    lng: initial,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: ALL_LOCALES,
    // 'ko-KR' → 'ko'
    load: 'languageOnly',
    interpolation: { escapeValue: false },
    returnEmptyString: false,
    react: { useSuspense: false },
  });

  // 나머지 4개 언어 사전 로드 (백그라운드)
  void preloadLocales(ALL_LOCALES.filter((l) => l !== initial));

  // 스크린리더가 읽는 언어를 실제 표시 언어에 맞춘다 (접근성, PLAN 5-7-1장).
  // 이벤트에 hooking하는 이유: `initI18n`의 최초 언어와 `changeLocale`의 전환을
  // 둘 다 한 곳에서 처리하려고 — `<html lang>`가 "ko"에 고정되면 일본어 화면을
  // 한국어 음성으로 읽는다.
  i18n.on('languageChanged', applyDocumentLang);

  initialized = true;
  return i18n;
}

/** 지정 언어로 전환. 필요 시 지연 번들을 로드한다. */
export async function changeLocale(locale: Locale): Promise<void> {
  if (!i18n.hasResourceBundle(locale, 'translation')) {
    const loader = LAZY_LOADERS[locale];
    if (loader) {
      const bundle = await loader();
      i18n.addResourceBundle(locale, 'translation', bundle, true, true);
    }
  }
  await i18n.changeLanguage(locale);
  applyDocumentLang(locale);
}

/** 앱 시작 시 나머지 로케일을 백그라운드로 미리 받는다. */
export async function preloadLocales(locales: Locale[]): Promise<void> {
  await Promise.all(
    locales.map(async (locale) => {
      if (i18n.hasResourceBundle(locale, 'translation')) return;
      const loader = LAZY_LOADERS[locale];
      if (!loader) return;
      const bundle = await loader();
      i18n.addResourceBundle(locale, 'translation', bundle, true, true);
    }),
  );
}

function applyDocumentLang(locale: Locale): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = locale;
}

function getNavigatorLanguages(): readonly string[] | undefined {
  if (typeof navigator === 'undefined') return undefined;
  return navigator.languages ?? [navigator.language];
}

export { i18n };
export * from '@talk2nick/core';
export type { Locale };
